import React, { useState } from 'react';
import { Phone, Mail, MapPin, Send, CheckCircle, Loader2 } from 'lucide-react';
import { companyInfo, services } from '../data/mock';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    job_address: '',
    services: [],
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleServiceToggle = (serviceId) => {
    setFormData((prev) => ({
      ...prev,
      services: prev.services.includes(serviceId)
        ? prev.services.filter((s) => s !== serviceId)
        : [...prev.services, serviceId],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.services.length === 0) {
      toast.error('Please select at least one service');
      return;
    }
    
    setIsSubmitting(true);

    try {
      // Get service titles for the email
      const selectedServiceTitles = formData.services.map(
        (id) => services.find((s) => s.id === id)?.title || id
      );

      const response = await axios.post(`${BACKEND_URL}/api/quote-request`, {
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        job_address: formData.job_address,
        services: selectedServiceTitles,
        message: formData.message,
      });

      if (response.data.status === 'success') {
        setIsSubmitted(true);
        toast.success('Quote request submitted successfully!');

        // Reset form after 5 seconds
        setTimeout(() => {
          setFormData({
            name: '',
            phone: '',
            email: '',
            job_address: '',
            services: [],
            message: '',
          });
          setIsSubmitted(false);
        }, 5000);
      }
    } catch (error) {
      console.error('Error submitting quote request:', error);
      toast.error('Something went wrong. Please call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="section-padding bg-[#1B3A3A]">
      <div className="container mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Contact Info */}
          <div>
            <span className="text-[#C5D89A] font-semibold text-sm uppercase tracking-wider">
              Get In Touch
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mt-3 mb-6">
              Request a Free Quote
            </h2>
            <p className="text-white/80 text-lg mb-10">
              Ready to transform your outdoor space? Contact us today for a free
              estimate. We serve both residential and commercial properties
              throughout Georgia and Florida.
            </p>

            {/* Contact Cards */}
            <div className="space-y-4 mb-10">
              <a
                href={`tel:${companyInfo.phone}`}
                className="flex items-center gap-4 p-4 bg-white/10 rounded-2xl hover:bg-white/15 transition-colors group"
              >
                <div className="p-3 bg-[#C5D89A] rounded-xl text-[#1B3A3A]">
                  <Phone size={24} />
                </div>
                <div>
                  <p className="text-white/60 text-sm">Call Us</p>
                  <p className="text-white font-semibold text-lg group-hover:text-[#C5D89A] transition-colors">
                    {companyInfo.phone}
                  </p>
                </div>
              </a>

              <a
                href={`mailto:${companyInfo.email}`}
                className="flex items-center gap-4 p-4 bg-white/10 rounded-2xl hover:bg-white/15 transition-colors group"
              >
                <div className="p-3 bg-[#C5D89A] rounded-xl text-[#1B3A3A]">
                  <Mail size={24} />
                </div>
                <div>
                  <p className="text-white/60 text-sm">Email Us</p>
                  <p className="text-white font-semibold group-hover:text-[#C5D89A] transition-colors">
                    {companyInfo.email}
                  </p>
                </div>
              </a>

              <div className="flex items-center gap-4 p-4 bg-white/10 rounded-2xl">
                <div className="p-3 bg-[#C5D89A] rounded-xl text-[#1B3A3A]">
                  <MapPin size={24} />
                </div>
                <div>
                  <p className="text-white/60 text-sm">Our Location</p>
                  <p className="text-white font-semibold">
                    {companyInfo.address}
                  </p>
                  <p className="text-[#C5D89A] text-sm mt-1">
                    {companyInfo.serviceArea}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white rounded-3xl p-8 lg:p-10 shadow-xl">
            {isSubmitted ? (
              <div className="h-full flex flex-col items-center justify-center text-center py-10">
                <div className="w-20 h-20 bg-[#C5D89A] rounded-full flex items-center justify-center mb-6 animate-scale-in">
                  <CheckCircle className="text-[#1B3A3A]" size={40} />
                </div>
                <h3 className="text-2xl font-bold text-[#1B3A3A] mb-3">
                  Thank You!
                </h3>
                <p className="text-[#6B7B7B]">
                  We've received your quote request and will contact you shortly.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <h3 className="text-2xl font-bold text-[#1B3A3A] mb-6">
                  Request a Quote
                </h3>

                {/* Name */}
                <div>
                  <label className="block text-[#1B3A3A] font-medium mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="form-input"
                    placeholder="Your full name"
                  />
                </div>

                {/* Phone and Email */}
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-[#1B3A3A] font-medium mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="form-input"
                      placeholder="(555) 123-4567"
                    />
                  </div>
                  <div>
                    <label className="block text-[#1B3A3A] font-medium mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="form-input"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>

                {/* Job Location Address */}
                <div>
                  <label className="block text-[#1B3A3A] font-medium mb-2">
                    Job Location Address *
                  </label>
                  <input
                    type="text"
                    name="job_address"
                    value={formData.job_address}
                    onChange={handleChange}
                    required
                    className="form-input"
                    placeholder="Full address where service is needed"
                  />
                </div>

                {/* Services Selection */}
                <div>
                  <label className="block text-[#1B3A3A] font-medium mb-3">
                    Services Needed * <span className="text-sm font-normal text-[#6B7B7B]">(Select all that apply)</span>
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {services.map((service) => (
                      <label
                        key={service.id}
                        className={`flex items-center gap-2 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                          formData.services.includes(service.id)
                            ? 'border-[#1B3A3A] bg-[#F5F9E8]'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={formData.services.includes(service.id)}
                          onChange={() => handleServiceToggle(service.id)}
                          className="sr-only"
                        />
                        <div
                          className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                            formData.services.includes(service.id)
                              ? 'bg-[#1B3A3A] border-[#1B3A3A]'
                              : 'border-gray-300'
                          }`}
                        >
                          {formData.services.includes(service.id) && (
                            <svg
                              className="w-3 h-3 text-white"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={3}
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                          )}
                        </div>
                        <span className="text-sm text-[#1B3A3A]">
                          {service.title}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Additional Message */}
                <div>
                  <label className="block text-[#1B3A3A] font-medium mb-2">
                    Additional Details
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    className="form-input form-textarea"
                    placeholder="Tell us more about your project (optional)"
                    rows={3}
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-accent w-full flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed py-4 text-lg"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="animate-spin" size={22} />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send size={22} />
                      Submit Quote Request
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
